import { CONFIG } from '../config';
import type { Bounds } from '../types';

// Input interface for Player
interface PlayerInput {
    getTouchDelta(): { active: boolean; dx: number; dy: number };
    resetTouchDelta(): void;
    getAxis(axis: 'horizontal' | 'vertical'): number;
}

// 플레이어 진화 단계 (1~4)
export const PlayerStage = {
    TRIANGLE: 1,    // 1단계: 삼각형 (3변)
    SQUARE: 2,      // 2단계: 사각형 (4변)
    PENTAGON: 3,    // 3단계: 오각형 (5변)
    HEXAGON: 4      // 4단계: 육각형 (6변)
} as const;

export class Player {
    public x: number;
    public y: number;
    public radius: number;
    public speed: number;
    public color: string;
    public rotation: number;
    public rotationSpeed: number;
    public stage: number;
    public hasShield: boolean;

    constructor() {
        this.x = CONFIG.PLAYER.START_X;
        this.y = CONFIG.PLAYER.START_Y;
        this.radius = CONFIG.PLAYER.RADIUS;
        this.speed = CONFIG.PLAYER.SPEED;
        this.color = CONFIG.PLAYER.COLOR;
        this.rotation = 0;
        this.rotationSpeed = Math.PI * 2;
        this.stage = PlayerStage.TRIANGLE;
        this.hasShield = false;
        this.reset();
    }

    reset(): void {
        this.x = CONFIG.PLAYER.START_X;
        this.y = CONFIG.PLAYER.START_Y;
        this.radius = CONFIG.PLAYER.RADIUS;
        this.speed = CONFIG.PLAYER.SPEED;
        this.color = CONFIG.PLAYER.COLOR;
        this.rotation = 0; // 회전 각도 (라디안)
        this.rotationSpeed = Math.PI * 2; // 1초에 1회전 (2π 라디안)

        // 플레이어 진화 단계 (1~4)
        this.stage = PlayerStage.TRIANGLE;

        // 보호막 상태 (별 아이템으로 활성화, 1회성)
        this.hasShield = false;
    }

    update(
        deltaTime: number,
        input: PlayerInput,
        canvasWidth: number = CONFIG.CANVAS_WIDTH,
        canvasHeight: number = CONFIG.CANVAS_HEIGHT
    ): void {
        // Check for touch input first
        const touchDelta = input.getTouchDelta();

        if (touchDelta.active) {
            // Move by the same amount the touch moved
            this.x += touchDelta.dx;
            this.y += touchDelta.dy;

            // Reset delta after consuming it
            input.resetTouchDelta();
        } else {
            // Keyboard/gamepad input uses axis-based movement
            const moveX = input.getAxis('horizontal');
            const moveY = input.getAxis('vertical');

            this.x += moveX * this.speed * deltaTime;
            this.y += moveY * this.speed * deltaTime;
        }

        // Update rotation (오른쪽으로 회전)
        this.rotation += this.rotationSpeed * deltaTime;

        // Clamp to play area bounds (inside white border)
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const minX = margin + this.radius;
        const maxX = canvasWidth - margin - this.radius;
        const minY = margin + this.radius;
        const maxY = canvasHeight - margin - this.radius;

        this.x = Math.max(minX, Math.min(maxX, this.x));
        this.y = Math.max(minY, Math.min(maxY, this.y));
    }

    render(
        ctx: CanvasRenderingContext2D,
        color?: string,
        opacity: number = 1.0,
        lineWidth: number = 1
    ): void {
        const strokeColor = color || this.color;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.globalAlpha = opacity;
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = lineWidth;

        // 진화 단계에 따라 다각형 렌더링
        this.renderPolygon(ctx, this.stage + 2); // stage 1=삼각형(3), 2=사각형(4), 3=오각형(5), 4=육각형(6)

        // 보호막이 있을 때 원형 표시
        if (this.hasShield) {
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = lineWidth;
            ctx.globalAlpha = opacity * 0.5; // 보호막은 플레이어의 절반 투명도
            ctx.beginPath();
            ctx.arc(0, 0, this.radius * 1.5, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.restore();
    }

    /**
     * n각형 렌더링 (n = 3~6)
     */
    renderPolygon(ctx: CanvasRenderingContext2D, sides: number): void {
        const size = this.radius;
        ctx.beginPath();

        // 정다각형의 꼭지점 계산
        for (let i = 0; i < sides; i++) {
            const angle = (i * 2 * Math.PI / sides) - Math.PI / 2; // -90도부터 시작 (위쪽)
            const px = Math.cos(angle) * size;
            const py = Math.sin(angle) * size;

            if (i === 0) {
                ctx.moveTo(px, py);
            } else {
                ctx.lineTo(px, py);
            }
        }

        ctx.closePath();
        ctx.stroke();
    }

    // 보호막 활성화 (별 아이템)
    activateShield(): void {
        this.hasShield = true;
    }

    // 보호막 사용 (충돌 시 1회 방어)
    useShield(): boolean {
        if (this.hasShield) {
            this.hasShield = false;
            return true;
        }
        return false;
    }

    // 진화 (다음 단계로 업그레이드)
    evolve(): boolean {
        if (this.stage < PlayerStage.HEXAGON) {
            this.stage++;
            return true;
        }
        return false; // 이미 최대 단계
    }

    // 퇴화 (이전 단계로 다운그레이드)
    devolve(): boolean {
        if (this.stage > PlayerStage.TRIANGLE) {
            this.stage--;
            return true;
        }
        return false; // 이미 최소 단계 (게임오버)
    }

    // 현재 단계 반환
    getStage(): number {
        return this.stage;
    }

    // 최소 단계(삼각형)인지 확인
    isMinStage(): boolean {
        return this.stage === PlayerStage.TRIANGLE;
    }

    // 최대 단계(육각형)인지 확인
    isMaxStage(): boolean {
        return this.stage === PlayerStage.HEXAGON;
    }

    getBounds(): Bounds {
        return {
            x: this.x,
            y: this.y,
            radius: this.radius
        };
    }
}
